import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkle, CircleNotch, FloppyDisk } from "@phosphor-icons/react";
import { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { useMediaPoll } from "./TranscribeUploadModal";

export function TranscriptPanel({ client, media, recordId, onMediaChanged, onRecordChanged, readOnly }) {
  const [summary, setSummary] = useState(media.ai_summary || "");
  const [confirmMode, setConfirmMode] = useState(null);
  const status = media.transcription_status || "idle";

  useEffect(() => { setSummary(media.ai_summary || ""); }, [media.ai_summary]);
  useMediaPoll(client, media, onMediaChanged, (m) => m.transcription_status === "processing");

  const start = async () => {
    try { await client.transcribe(media.id); onMediaChanged({ ...media, transcription_status: "processing" }); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not start transcription")); }
  };
  const saveSummary = async () => {
    try { await client.saveSummary(media.id, summary); onMediaChanged({ ...media, ai_summary: summary }); toast.success("Summary saved"); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not save summary")); }
  };
  const apply = async () => {
    try {
      await api.post(`/service/records/${recordId}/apply-summary`, { text: summary, mode: confirmMode, media_id: media.id });
      toast.success(confirmMode === "replace" ? "Notes replaced with summary" : "Summary appended to notes");
      onRecordChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Could not update notes")); }
    setConfirmMode(null);
  };

  if (readOnly && !media.ai_summary) return null;
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2.5" data-testid="transcript-panel">
      {!readOnly && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" onClick={start} disabled={status === "processing"} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="video-transcribe-btn">
            {status === "processing" ? <CircleNotch size={14} className="mr-1.5 animate-spin" /> : <Sparkle size={14} weight="fill" className="mr-1.5" />}
            {status === "processing" ? "Transcribing…" : status === "done" ? "Re-run Transcribe & Summarize" : "Transcribe & Summarize"}
          </Button>
          <span className="text-[11px] text-slate-500" data-testid="video-transcribe-status">
            {status === "processing" && "Transcribing and summarizing — this can take a minute."}
            {status === "no_speech" && (media.transcription_error || "No intelligible speech detected.")}
            {status === "failed" && <span className="text-rose-600">Failed: {media.transcription_error}</span>}
          </span>
        </div>
      )}
      {(status === "done" || readOnly) && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-violet-600">
            <Sparkle size={12} weight="fill" /> AI-generated summary{readOnly ? "" : " — review before adding to notes"}
          </div>
          {readOnly ? <div className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="video-summary-text">{media.ai_summary}</div> : (
            <>
              {!media.ai_summary && !summary && <div className="text-xs text-slate-500">The recording didn't describe any service work, so no summary was written.</div>}
              <Textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="video-summary-input" />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={saveSummary} disabled={summary === (media.ai_summary || "")} data-testid="video-summary-save"><FloppyDisk size={13} className="mr-1" /> Save edit</Button>
                {recordId && <Button size="sm" onClick={() => setConfirmMode("append")} disabled={!summary.trim()} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="video-summary-append">Append to notes</Button>}
                {recordId && <Button size="sm" variant="outline" onClick={() => setConfirmMode("replace")} disabled={!summary.trim()} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="video-summary-replace">Replace notes</Button>}
              </div>
            </>
          )}
        </div>
      )}
      <AlertDialog open={!!confirmMode} onOpenChange={(v) => !v && setConfirmMode(null)}>
        <AlertDialogContent data-testid="summary-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmMode === "replace" ? "Replace existing notes?" : "Append summary to notes?"}</AlertDialogTitle>
            <AlertDialogDescription>{confirmMode === "replace" ? "The current service notes will be overwritten by this AI summary." : "The summary will be added below the current notes, labelled as an AI summary."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="summary-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={apply} className={confirmMode === "replace" ? "bg-rose-600 hover:bg-rose-700" : ""} data-testid="summary-confirm-ok">{confirmMode === "replace" ? "Replace" : "Append"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
